/**
 * GDPR Tab Component
 *
 * Admin interface for managing GDPR requests:
 * - Account deletion requests (Art. 17 - Right to erasure)
 * - Data export requests (Art. 20 - Right to data portability)
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getAdminDeletionRequests,
  getAdminDataExports,
  expediteDeletion,
  type AdminDeletionRequest,
  type AdminDataExport,
} from '@/services/adminApi';

type SubTab = 'deletion' | 'exports';

export default function GDPRTab() {
  const { t } = useTranslation('admin');
  const [activeSubTab, setActiveSubTab] = useState<SubTab>('deletion');
  const [deletionRequests, setDeletionRequests] = useState<AdminDeletionRequest[]>([]);
  const [dataExports, setDataExports] = useState<AdminDataExport[]>([]);
  const [deletionCounts, setDeletionCounts] = useState({ pending: 0, cancelled: 0, executed: 0 });
  const [exportCounts, setExportCounts] = useState({ pending: 0, processing: 0, ready: 0, failed: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('');

  const fetchDeletionRequests = async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await getAdminDeletionRequests(filterStatus || undefined);
      setDeletionRequests(data.requests);
      setDeletionCounts({
        pending: data.pending_count,
        cancelled: data.cancelled_count,
        executed: data.executed_count,
      });
    } catch (err: any) {
      setError(err.response?.data?.detail || t('common.error'));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDataExports = async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await getAdminDataExports(filterStatus || undefined);
      setDataExports(data.exports);
      setExportCounts({
        pending: data.pending_count,
        processing: data.processing_count,
        ready: data.ready_count,
        failed: data.failed_count,
      });
    } catch (err: any) {
      setError(err.response?.data?.detail || t('common.error'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (activeSubTab === 'deletion') {
      fetchDeletionRequests();
    } else {
      fetchDataExports();
    }
  }, [activeSubTab, filterStatus]);

  const handleExpedite = async (requestId: string) => {
    if (!confirm(t('gdpr.expediteConfirm'))) {
      return;
    }
    try {
      await expediteDeletion(requestId);
      alert(t('gdpr.expediteSuccess'));
      fetchDeletionRequests();
    } catch (err: any) {
      alert(err.response?.data?.detail || t('common.error'));
    }
  };

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800',
      cancelled: 'bg-gray-100 text-gray-800',
      executed: 'bg-red-100 text-red-800',
      processing: 'bg-blue-100 text-blue-800',
      ready: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      expired: 'bg-gray-100 text-gray-800',
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-6">
      {/* Sub-tabs */}
      <div className="flex space-x-4 border-b border-gray-200">
        <button
          onClick={() => { setActiveSubTab('deletion'); setFilterStatus(''); }}
          className={`pb-2 px-1 font-medium text-sm transition-colors ${
            activeSubTab === 'deletion'
              ? 'border-b-2 border-red-500 text-red-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          {t('gdpr.deletionRequests')}
          {deletionCounts.pending > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-800 text-xs rounded-full">
              {deletionCounts.pending}
            </span>
          )}
        </button>
        <button
          onClick={() => { setActiveSubTab('exports'); setFilterStatus(''); }}
          className={`pb-2 px-1 font-medium text-sm transition-colors ${
            activeSubTab === 'exports'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          {t('gdpr.dataExports')}
          {(exportCounts.pending + exportCounts.processing) > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">
              {exportCounts.pending + exportCounts.processing}
            </span>
          )}
        </button>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-4">
        <label className="text-sm text-gray-600">{t('gdpr.filterByStatus')}</label>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">{t('gdpr.all')}</option>
          {activeSubTab === 'deletion' ? (
            <>
              <option value="pending">{t('gdpr.pending')} ({deletionCounts.pending})</option>
              <option value="cancelled">{t('gdpr.cancelled')} ({deletionCounts.cancelled})</option>
              <option value="executed">{t('gdpr.executed')} ({deletionCounts.executed})</option>
            </>
          ) : (
            <>
              <option value="pending">{t('gdpr.pending')} ({exportCounts.pending})</option>
              <option value="processing">{t('gdpr.processing')} ({exportCounts.processing})</option>
              <option value="ready">{t('gdpr.ready')} ({exportCounts.ready})</option>
              <option value="failed">{t('gdpr.failed')} ({exportCounts.failed})</option>
            </>
          )}
        </select>
        <button
          onClick={() => activeSubTab === 'deletion' ? fetchDeletionRequests() : fetchDataExports()}
          className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
          title={t('gdpr.refresh')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Loading state */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : activeSubTab === 'deletion' ? (
        /* Deletion Requests Table */
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('gdpr.user')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('gdpr.status')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('gdpr.requested')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('gdpr.scheduledDeletion')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('gdpr.reason')}</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('gdpr.actions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {deletionRequests.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    {t('gdpr.noDeletionRequests')}
                  </td>
                </tr>
              ) : (
                deletionRequests.map((req) => (
                  <tr key={req.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{req.full_name || req.username}</div>
                      <div className="text-sm text-gray-500">{req.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(req.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(req.requested_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(req.scheduled_deletion_at)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                      {req.reason || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {req.status === 'pending' && (
                        <button
                          onClick={() => handleExpedite(req.id)}
                          className="text-red-600 hover:text-red-900 bg-red-50 hover:bg-red-100 px-3 py-1 rounded-lg transition-colors"
                        >
                          {t('gdpr.expedite')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : (
        /* Data Exports Table */
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('gdpr.user')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('gdpr.status')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('gdpr.format')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('gdpr.requested')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('gdpr.completed')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('gdpr.size')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {dataExports.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    {t('gdpr.noDataExports')}
                  </td>
                </tr>
              ) : (
                dataExports.map((exp) => (
                  <tr key={exp.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{exp.username}</div>
                      <div className="text-sm text-gray-500">{exp.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(exp.status)}
                      {exp.error_message && (
                        <div className="text-xs text-red-500 mt-1">{exp.error_message}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {exp.format.toUpperCase()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(exp.requested_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {exp.completed_at ? formatDate(exp.completed_at) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatFileSize(exp.file_size_bytes)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {activeSubTab === 'deletion' ? (
          <>
            <div className="bg-yellow-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-yellow-700">{deletionCounts.pending}</div>
              <div className="text-sm text-yellow-600">{t('gdpr.pending')}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-gray-700">{deletionCounts.cancelled}</div>
              <div className="text-sm text-gray-600">{t('gdpr.cancelled')}</div>
            </div>
            <div className="bg-red-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-red-700">{deletionCounts.executed}</div>
              <div className="text-sm text-red-600">{t('gdpr.executed')}</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-blue-700">
                {deletionCounts.pending + deletionCounts.cancelled + deletionCounts.executed}
              </div>
              <div className="text-sm text-blue-600">{t('gdpr.total')}</div>
            </div>
          </>
        ) : (
          <>
            <div className="bg-yellow-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-yellow-700">{exportCounts.pending + exportCounts.processing}</div>
              <div className="text-sm text-yellow-600">{t('gdpr.inProgress')}</div>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-green-700">{exportCounts.ready}</div>
              <div className="text-sm text-green-600">{t('gdpr.ready')}</div>
            </div>
            <div className="bg-red-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-red-700">{exportCounts.failed}</div>
              <div className="text-sm text-red-600">{t('gdpr.failed')}</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-blue-700">
                {exportCounts.pending + exportCounts.processing + exportCounts.ready + exportCounts.failed}
              </div>
              <div className="text-sm text-blue-600">{t('gdpr.total')}</div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
